import React, { useEffect, useRef, useState } from "react";
import { useParams, useSearchParams, Link } from "react-router-dom";
import { api, formatApiError } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import { MapPin, Send, Star, CreditCard, Loader2, CheckCircle2, FileText } from "lucide-react";
import MissionMap from "../components/MissionMap";
import ProofOfDeliveryDialog from "../components/ProofOfDeliveryDialog";
import PickupProofDialog from "../components/PickupProofDialog";
import ShipperConfirmDialog from "../components/ShipperConfirmDialog";
import NavigationView from "../components/NavigationView";
import MissionDisputePanel from "../components/MissionDisputePanel";
import MissionInvoices from "../components/MissionInvoices";
import DriverLocationControl from "../components/DriverLocationControl";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { Label } from "../components/ui/label";
import { VEHICLE_LABEL } from "../lib/pricing";
import { BackButton } from "../components/BackButton";

const STATUS_FLOW = [
  { key: "open", label: "Publiée" },
  { key: "accepted", label: "Acceptée" },
  { key: "loaded", label: "Chargée" },
  { key: "en_route", label: "En route" },
  { key: "delivered", label: "Livrée" },
];

const PAYMENT_BADGES = {
  unpaid: { label: "Non payée", color: "bg-neutral-200 text-neutral-800" },
  pending: { label: "Paiement en cours", color: "bg-amber-500 text-white" },
  paid: { label: "Payée — reversement en attente", color: "bg-blue-600 text-white" },
  payout_pending: { label: "Versement en cours", color: "bg-amber-500 text-white" },
  released: { label: "Reversée", color: "bg-emerald-600 text-white" },
  refund_pending: { label: "Remboursement en cours", color: "bg-amber-500 text-white" },
  refunded: { label: "Remboursée", color: "bg-red-600 text-white" },
  partial_refunded: { label: "Remboursement partiel", color: "bg-orange-500 text-white" },
  partially_refunded: { label: "Remboursement partiel", color: "bg-orange-500 text-white" },
};

export default function MissionDetail() {
  const demoMode = process.env.REACT_APP_DEMO_MODE === "1";
  const { id } = useParams();
  const { user } = useAuth();
  const [params, setParams] = useSearchParams();
  const [m, setM] = useState(null);
  const [messages, setMessages] = useState([]);
  const [text, setText] = useState("");
  const [loading, setLoading] = useState(true);
  const [payLoading, setPayLoading] = useState(false);
  const [pollingPayment, setPollingPayment] = useState(false);
  const [proofOpen, setProofOpen] = useState(false);
  const [proofLoading, setProofLoading] = useState(false);
  const [pickupProofOpen, setPickupProofOpen] = useState(false);
  const [pickupProofLoading, setPickupProofLoading] = useState(false);
  const [cancelOpen, setCancelOpen] = useState(false);
  const [cancelReason, setCancelReason] = useState("");
  const [cancelLoading, setCancelLoading] = useState(false);
  const [navOpen, setNavOpen] = useState(false);
  const [navPhase, setNavPhase] = useState("pickup"); // "pickup" | "delivery"
  const [shipperConfirmOpen, setShipperConfirmOpen] = useState(false);
  const [shipperConfirmLoading, setShipperConfirmLoading] = useState(false);
  const [candidatures, setCandidatures] = useState([]);
  const [selectionCandidate, setSelectionCandidate] = useState(null);
  const [transporterProfile, setTransporterProfile] = useState(null);
  const [assignmentPlate, setAssignmentPlate] = useState("");
  const [assignmentSaving, setAssignmentSaving] = useState(false);
  const [contract, setContract] = useState(null);
  const [contractOpen, setContractOpen] = useState(false);
  const [contractLoading, setContractLoading] = useState(false);

  const handleShipperConfirm = async ({ signature, signed_name }) => {
    setShipperConfirmLoading(true);
    try {
      await api.post(`/missions/${id}/confirm-receipt`, { signature, signed_name });
      toast.success("Réception confirmée — consultez le statut du versement");
      setShipperConfirmOpen(false);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setShipperConfirmLoading(false); }
  };
  const chatEnd = useRef(null);

  const load = async () => {
    try {
      const [r1, r2, r3] = await Promise.all([
        api.get(`/missions/${id}`),
        api.get(`/missions/${id}/messages`).catch(() => ({ data: [] })),
        api.get("/transporter/profile").catch(() => ({ data: null })),
      ]);
      setM(r1.data);
      setMessages(r2.data || []);
      setTransporterProfile(r3.data || null);
      if (r3.data?.vehicle_plate) setAssignmentPlate((old) => old || r3.data.vehicle_plate);
      // L'expéditeur propriétaire charge la liste des candidats tant que la mission est ouverte
      if (r1.data && r1.data.status === "open" && user?.id === r1.data.shipper_id) {
        try {
          const rc = await api.get(`/missions/${id}/candidatures`);
          setCandidatures(rc.data?.candidatures || []);
        } catch { setCandidatures([]); }
      } else {
        setCandidatures([]);
      }
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [id, user?.id]);
  useEffect(() => { chatEnd.current?.scrollIntoView({ behavior: "smooth" }); }, [messages]);

  // Auto-ouverture de la navigation quand on arrive depuis l'acceptation (?autonav=1)
  useEffect(() => {
    if (!m) return;
    if (params.get("autonav") !== "1") return;
    // Détermine la phase selon le statut courant
    const phase = (m.status === "loaded" || m.status === "en_route") ? "delivery" : "pickup";
    setNavPhase(phase);
    setNavOpen(true);
    // Retire le param après usage pour ne pas rouvrir à chaque rerender
    const next = new URLSearchParams(params);
    next.delete("autonav");
    setParams(next, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [m?.id, params.get("autonav")]);

  // Poll payment status when returning from Stripe with session_id in URL
  useEffect(() => {
    const sessionId = params.get("session_id");
    const paymentResult = params.get("payment");
    if (paymentResult === "cancelled") {
      // Le cancel_url Stripe ne renvoie pas toujours le session_id : on
      // réouvre donc explicitement la mission pour permettre un nouvel essai.
      api.post("/payments/cancel", { mission_id: id })
        .catch(() => {})
        .finally(() => {
          toast.error("Paiement annulé — vous pouvez réessayer");
          setParams({}, { replace: true });
          load();
        });
      return;
    }
    if (!sessionId) return;
    setPollingPayment(true);
    let attempts = 0;
    const MAX = 8;
    const poll = async () => {
      try {
        const { data } = await api.get(`/payments/status/${sessionId}`);
        if (data.payment_status === "paid") {
          toast.success("Paiement confirmé");
          setPollingPayment(false);
          setParams({}, { replace: true });
          load();
          return;
        }
        if (data.status === "expired") {
          toast.error("Session expirée");
          setPollingPayment(false);
          setParams({}, { replace: true });
          return;
        }
      } catch (e) {
        // Retry silently — le polling continue. Loggé pour debug, pas de toast
        // car l'utilisateur peut juste être hors ligne brièvement.
        console.debug("Payment poll attempt failed, will retry:", e?.message);
      }
      attempts++;
      if (attempts < MAX) setTimeout(poll, 2000);
      else { setPollingPayment(false); toast.info("Vérification en cours — actualisez la page dans un instant"); }
    };
    poll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [params.get("session_id"), params.get("payment")]);

  if (loading) return <div className="max-w-7xl mx-auto px-6 py-10 font-mono text-sm text-muted-foreground">CHARGEMENT…</div>;
  if (!m) return <div className="max-w-7xl mx-auto px-6 py-10">Mission introuvable</div>;

  const applyMission = async () => {
    try {
      const { data } = await api.post(`/missions/${id}/apply`);
      toast.success(`Candidature envoyée — valable ${data?.validity_minutes || 60} min. L'expéditeur choisira parmi les candidats.`);
      await load();
    }
    catch (e) { toast.error(formatApiError(e)); }
  };

  const withdrawMission = async () => {
    try {
      await api.post(`/missions/${id}/withdraw`);
      toast.success("Candidature retirée");
      await load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const selectTransporter = async (transporterId) => {
    try {
      await api.post(`/missions/${id}/select`, { transporter_id: transporterId });
      toast.success("Transporteur choisi — procédez au paiement pour confirmer le contrat");
      await load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const confirmAssignment = async () => {
    if (!assignmentPlate.trim()) {
      toast.error("Déclarez la plaque du véhicule vérifié dans votre profil");
      return;
    }
    setAssignmentSaving(true);
    try {
      await api.post(`/missions/${id}/assignment`, {
        driver_id: user?.id,
        driver_name: user?.name || "Conducteur titulaire",
        vehicle_plate: assignmentPlate.trim(),
        vehicle_type: m.vehicle_type,
      });
      toast.success("Conducteur et véhicule déclarés pour cette mission");
      await load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setAssignmentSaving(false); }
  };

  const updateStatus = async (st, extraBody = {}) => {
    try {
      await api.post(`/missions/${id}/status`, { status: st, ...extraBody });
      toast.success("Statut mis à jour");
      load();
      setProofOpen(false);
      setPickupProofOpen(false);
      // Auto-ouverture de la navigation vers la LIVRAISON quand le colis est chargé / en route
      if (st === "loaded" || st === "en_route") {
        setNavPhase("delivery");
        setNavOpen(true);
      }
    }
    catch (e) { toast.error(formatApiError(e)); }
    finally { setProofLoading(false); setPickupProofLoading(false); }
  };

  const confirmDelivery = async (proof) => {
    setProofLoading(true);
    await updateStatus("delivered", proof);
  };

  const confirmPickup = async (proof) => {
    setPickupProofLoading(true);
    await updateStatus("loaded", proof);
  };

  const reportAbsence = async () => {
    if (!window.confirm("Signaler l'absence du transporteur et remettre la mission en recherche ?")) return;
    try {
      const { data } = await api.post(`/missions/${id}/report-absence`, { reason: "Transporteur absent au rendez-vous" });
      toast.success(data?.refund?.ok === false ? "Mission remise en recherche ; remboursement à traiter" : "Mission remise en recherche");
      await load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const viewContract = async () => {
    setContractLoading(true);
    try {
      const { data } = await api.get(`/missions/${id}/contract`);
      setContract(data);
      setContractOpen(true);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setContractLoading(false); }
  };

  const payNow = async () => {
    setPayLoading(true);
    try {
      if (m?.payment_status === "pending") {
        await api.post("/payments/cancel", { mission_id: id });
      }
      const { data } = await api.post(`/payments/checkout`, {
        mission_id: id,
        origin_url: window.location.origin,
      });
      window.location.href = data.url; // redirect to Stripe
    } catch (e) {
      toast.error(formatApiError(e));
      setPayLoading(false);
    }
  };

  const sendMessage = async (e) => {
    e.preventDefault();
    if (!text.trim()) return;
    try {
      const { data } = await api.post(`/missions/${id}/messages`, { text });
      setMessages((prev) => [...prev, data]);
      setText("");
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const cancelMission = async () => {
    setCancelLoading(true);
    try {
      await api.post(`/missions/${id}/cancel`, { reason: cancelReason });
      toast.success("Mission annulée");
      setCancelOpen(false);
      setCancelReason("");
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setCancelLoading(false);
    }
  };

  const canChat = user?.id === m.shipper_id || user?.id === m.transporter_id;
  const isTransporter = user?.id === m.transporter_id;
  const currentIdx = STATUS_FLOW.findIndex((s) => s.key === m.status);
  const pickupTimestamp = m.pickup_date ? new Date(m.pickup_date).getTime() : NaN;
  const canReportAbsence = user?.id === m.shipper_id && m.status === "accepted"
    && Number.isFinite(pickupTimestamp) && Date.now() >= pickupTimestamp;

  return (
    <div className="max-w-7xl mx-auto px-6 py-10" data-testid="mission-detail">
      <BackButton to={user?.role === "transporter" ? "/transporter" : "/shipper"} label="Retour au tableau de bord" />
      <div className="overline text-neutral-500 mb-2">— Mission #{m.id.slice(0, 8)}</div>
      <div className="flex flex-wrap justify-between items-start gap-4 mb-8">
        <div>
          <h1 className="font-display font-bold text-3xl md:text-4xl tracking-tighter max-w-2xl">{m.title}</h1>
          {m.payment_status && PAYMENT_BADGES[m.payment_status] && (
            <span className={`inline-block mt-3 px-3 py-1 text-xs font-mono rounded-md ${PAYMENT_BADGES[m.payment_status].color}`}
              data-testid="payment-status-badge">
              💳 {PAYMENT_BADGES[m.payment_status].label}
            </span>
          )}
        </div>
        <div className="text-right">
          <div className="font-display font-bold text-3xl">{m.price}€</div>
          {m.platform_commission !== undefined && (
            <div className="text-xs text-neutral-500 font-mono mt-1">
              Commission {Math.round((m.commission_rate || 0.15) * 100)}% : {m.platform_commission}€ · Transporteur : {m.transporter_amount}€
            </div>
          )}
        </div>
      </div>

      {pollingPayment && (
        <div className="p-4 mb-6 rounded-md bg-blue-50 border border-blue-200 flex items-center gap-3" data-testid="payment-polling">
          <Loader2 className="w-5 h-5 animate-spin text-blue-700" />
          <div className="text-sm text-blue-900">Vérification du paiement en cours…</div>
        </div>
      )}

      {/* Status timeline */}
      <div className="border border-[#1F4032]/10 p-5 mb-8 overflow-x-auto">
        <div className="flex items-center min-w-max gap-3">
          {STATUS_FLOW.map((s, i) => (
            <React.Fragment key={s.key}>
              <div className={`flex flex-col items-center gap-1 ${i <= currentIdx ? "" : "opacity-30"}`}>
                <div className={`w-8 h-8 flex items-center justify-center font-mono text-xs ${i <= currentIdx ? "bg-[#1F4032] text-white" : "bg-muted"}`}>
                  {i + 1}
                </div>
                <div className="text-xs">{s.label}</div>
              </div>
              {i < STATUS_FLOW.length - 1 && (
                <div className={`h-px flex-1 min-w-8 ${i < currentIdx ? "bg-primary" : "bg-[#1F4032]/10"}`} />
              )}
            </React.Fragment>
          ))}
        </div>
      </div>

      <div className="grid md:grid-cols-3 gap-6">
        <div className="md:col-span-2 space-y-6">
          <div className="border border-[#1F4032]/10 p-5">
            <h3 className="overline text-neutral-500 mb-3">Itinéraire</h3>
            <div className="grid md:grid-cols-2 gap-5 items-start">
              <div>
                <div className="flex items-start gap-3 mb-3">
                  <MapPin className="w-4 h-4 text-neutral-500 mt-1" />
                  <div>
                    <div className="overline text-muted-foreground">Départ</div>
                    <div>{m.pickup_address}</div>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <MapPin className="w-4 h-4 text-black mt-1" />
                  <div>
                    <div className="overline text-muted-foreground">Arrivée</div>
                    <div>{m.delivery_address}</div>
                  </div>
                </div>
                <div className="font-mono text-xs text-muted-foreground mt-4">
                  Distance: {m.distance_km} km
                </div>
              </div>
              {m.pickup_lat != null && m.delivery_lat != null && (
                <MissionMap mission={m} />
              )}
            </div>
          </div>

          <div className="border border-[#1F4032]/10 p-5">
            <h3 className="overline text-neutral-500 mb-3">Cargaison</h3>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
              <div><div className="overline text-muted-foreground">Poids</div>{m.cargo_weight_kg} kg</div>
              <div><div className="overline text-muted-foreground">Volume</div>{m.cargo_volume_m3} m³</div>
              <div><div className="overline text-muted-foreground">Véhicule</div>{VEHICLE_LABEL[m.vehicle_type] || m.vehicle_type}</div>
              <div><div className="overline text-muted-foreground">Date</div>{m.pickup_date?.slice(0, 16).replace("T", " ")}</div>
            </div>
            <p className="text-sm mt-4 whitespace-pre-wrap">{m.description}</p>
            {m.pickup_proof_photo && (
              <div className="mt-4 pt-3 border-t border-[#1F4032]/10">
                <div className="overline mb-2">État constaté au chargement</div>
                <img src={m.pickup_proof_photo} alt="État de la marchandise au chargement" className="w-full max-h-56 object-contain rounded-md border border-[#1F4032]/10 bg-neutral-50" data-testid="pickup-proof-photo" />
                {m.pickup_condition_notes && <div className="text-xs text-muted-foreground mt-1">{m.pickup_condition_notes}</div>}
              </div>
            )}
          </div>

          {/* Chat */}
          {canChat && m.transporter_id && (
            <div className="border border-[#1F4032]/10 flex flex-col h-96" data-testid="mission-chat">
              <div className="p-4 border-b border-[#1F4032]/10 overline text-neutral-500">Messagerie</div>
              <div className="flex-1 overflow-y-auto p-4 space-y-3">
                {messages.length === 0 && <div className="text-xs text-muted-foreground">Aucun message.</div>}
                {messages.map((msg) => (
                  <div key={msg.id} className={`flex ${msg.sender_id === user.id ? "justify-end" : "justify-start"}`}>
                    <div className={`max-w-[70%] p-3 ${msg.sender_id === user.id ? "bg-[#1F4032] text-white" : "bg-muted"}`}>
                      <div className="overline opacity-60 mb-1">{msg.sender_name}</div>
                      <div className="text-sm">{msg.text}</div>
                    </div>
                  </div>
                ))}
                <div ref={chatEnd} />
              </div>
              <form onSubmit={sendMessage} className="p-3 border-t border-[#1F4032]/10 flex gap-2">
                <Input value={text} onChange={(e) => setText(e.target.value)}
                  placeholder="Écrire un message…"
                  className="rounded-md h-10" data-testid="chat-input" />
                <Button type="submit" className="rounded-md bg-[#1F4032] hover:bg-[#2A5443]"
                  data-testid="chat-send">
                  <Send className="w-4 h-4" />
                </Button>
              </form>
            </div>
          )}
        </div>

        <div className="space-y-4">
          <div className="border border-[#1F4032]/10 p-5">
            <h3 className="overline text-neutral-500 mb-3">Expéditeur</h3>
            <div className="font-display font-bold">{m.shipper_name}</div>
            <div className="text-xs text-muted-foreground mt-1">{m.contact_phone}</div>
          </div>

          {m.transporter && (
            <div className="border border-[#1F4032]/10 p-5">
              <h3 className="overline text-neutral-500 mb-3">Transporteur</h3>
              <div className="font-display font-bold">{m.transporter.name}</div>
              <div className="text-xs text-muted-foreground mt-1">{m.transporter.phone || "—"}</div>
              <Link to={`/u/${m.transporter_id}`} className="text-xs text-[#1F4032] underline mt-2 inline-block" data-testid="view-transporter-profile">
                Voir le profil public →
              </Link>
            </div>
          )}

          {/* Actions */}
          <div className="border border-[#1F4032]/10 p-5 space-y-2 rounded-md">
            <h3 className="overline text-neutral-500 mb-2">Actions</h3>
            {/* Paiement shipper : mission acceptée + non payée */}
            {user?.id === m.shipper_id && m.status === "accepted" && m.payment_status === "unpaid" && (
              <Button onClick={payNow} disabled={payLoading}
                className="w-full rounded-full bg-[#1F4032] hover:bg-[#2A5443] text-white gap-2 h-12"
                data-testid="pay-now">
                {payLoading ? <><Loader2 className="w-4 h-4 animate-spin" /> Redirection…</>
                  : <><CreditCard className="w-4 h-4" /> {demoMode ? `Simuler le paiement de ${m.price}€` : `Payer ${m.price}€ via Stripe`}</>}
              </Button>
            )}
            {user?.id === m.shipper_id && m.payment_status === "pending" && (
              <div className="text-sm text-amber-700 flex items-center justify-between gap-2 px-2 py-2">
                <span className="flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" /> Paiement en cours de vérification</span>
                <Button onClick={payNow} disabled={payLoading} variant="outline" size="sm" className="rounded-md">
                  Réessayer
                </Button>
              </div>
            )}
            {user?.id === m.shipper_id && m.payment_status === "paid" && m.status !== "delivered" && (
              <div className="text-sm text-blue-700 flex items-center gap-2 px-2 py-2 bg-blue-50 rounded-md">
                <CheckCircle2 className="w-4 h-4" /> Paiement reçu — reversement après vérification de la livraison
              </div>
            )}
            {user?.id === m.shipper_id && m.payment_status === "released" && (
              <div className="text-sm text-emerald-700 flex items-center gap-2 px-2 py-2 bg-emerald-50 rounded-md">
                <CheckCircle2 className="w-4 h-4" /> Paiement libéré au transporteur
              </div>
            )}
            {m.transporter_id && ["accepted", "loaded", "en_route", "delivered"].includes(m.status) && (
              <Button
                onClick={viewContract}
                disabled={contractLoading}
                variant="outline"
                className="w-full rounded-md border-[#1F4032]/30 text-[#1F4032] gap-2"
                data-testid="view-contract">
                {contractLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <FileText className="w-4 h-4" />}
                Voir les conditions du contrat
              </Button>
            )}

            {user && user.role !== "admin" && user.id !== m.shipper_id && m.status === "open" && (
              m.my_candidature?.status === "active" ? (
                <div className="space-y-2" data-testid="detail-candidature-active">
                  <div className="text-sm text-emerald-800 bg-emerald-50 border border-emerald-200 rounded-md px-3 py-2">
                    Candidature envoyée — l'expéditeur choisira parmi les candidats.
                  </div>
                  <Button onClick={withdrawMission} variant="outline"
                    className="w-full rounded-md border-neutral-300"
                    data-testid="detail-withdraw">Retirer ma candidature</Button>
                </div>
              ) : m.my_candidature?.status === "selected" ? (
                <div className="text-sm text-white bg-[#1F4032] rounded-md px-3 py-2" data-testid="detail-candidature-selected">
                  Vous avez été choisi pour cette mission ✓
                </div>
              ) : (
                <Button onClick={applyMission}
                  className="w-full rounded-md bg-[#1F4032] hover:bg-[#2A5443]"
                  data-testid="detail-apply">Candidater à la mission</Button>
              )
            )}

            {/* Expéditeur : liste des candidats à choisir (mission ouverte) */}
            {user?.id === m.shipper_id && m.status === "open" && (
              <div className="space-y-2" data-testid="candidates-panel">
                <h4 className="font-display font-bold text-sm">Candidats ({candidatures.length})</h4>
                {candidatures.length === 0 ? (
                  <p className="text-sm text-neutral-500" data-testid="candidates-empty">
                    Aucune candidature pour l'instant. Les transporteurs proches vont être notifiés.
                  </p>
                ) : (
                  candidatures.map((c) => (
                    <div key={c.transporter_id} className="flex items-center justify-between gap-3 border border-[#1F4032]/10 rounded-md p-3" data-testid={`candidate-${c.transporter_id}`}>
                      <div className="min-w-0">
                        <div className="font-medium truncate">{c.transporter_name}</div>
                        <div className="text-xs text-neutral-500 flex items-center gap-2">
                          {(c.transporter_rating?.count || 0) > 0 ? (
                            <span className="inline-flex items-center gap-0.5"><Star className="w-3 h-3 fill-amber-500 text-amber-500" />{c.transporter_rating.average.toFixed(1)} ({c.transporter_rating.count})</span>
                          ) : <span>Nouveau transporteur</span>}
                          <span>· {c.transporter_delivered || 0} livrées</span>
                          {c.seconds_left != null && <span>· expire dans {Math.max(0, Math.floor(c.seconds_left / 60))} min</span>}
                        </div>
                      </div>
                      <Button onClick={() => setSelectionCandidate(c)}
                        className="rounded-md bg-[#1F4032] hover:bg-[#2A5443] shrink-0"
                        data-testid={`select-${c.transporter_id}`}>Choisir</Button>
                    </div>
                  ))
                )}
              </div>
            )}
            {isTransporter && m.status === "accepted" && m.payment_status === "paid" && (
              <>
                {!m.assignment_confirmed_at ? (
                  <div className="border border-amber-300 bg-amber-50 rounded-md p-3 space-y-2" data-testid="assignment-panel">
                    <div className="text-sm font-semibold text-amber-900">Déclarer l'affectation</div>
                    <p className="text-xs text-amber-800">
                      Indiquez le conducteur enregistré et le véhicule vérifié avant le chargement.
                    </p>
                    <div className="text-xs text-amber-900">Conducteur : <b>{user?.name || "Votre compte transporteur"}</b></div>
                    <Input
                      value={assignmentPlate}
                      onChange={(e) => setAssignmentPlate(e.target.value.toUpperCase())}
                      placeholder={transporterProfile?.vehicle_plate || "Plaque du véhicule vérifié"}
                      className="h-10 bg-white"
                      data-testid="assignment-plate"
                    />
                    <Button onClick={confirmAssignment} disabled={assignmentSaving}
                      className="w-full rounded-md bg-[#1F4032] hover:bg-[#2A5443]" data-testid="assignment-confirm">
                      {assignmentSaving ? "Enregistrement…" : "Confirmer conducteur et véhicule"}
                    </Button>
                  </div>
                ) : (
                  <div className="text-xs text-emerald-800 bg-emerald-50 border border-emerald-200 rounded-md px-3 py-2" data-testid="assignment-confirmed">
                    Conducteur : <b>{m.assigned_driver_name}</b> · Véhicule : <b>{m.assigned_vehicle_plate}</b>
                  </div>
                )}
                <Button
                  onClick={() => { setNavPhase("pickup"); setNavOpen(true); }}
                  variant="outline"
                  className="w-full rounded-md border-[#1F4032] text-[#1F4032] hover:bg-[#1F4032]/5 mb-2 gap-2"
                  data-testid="open-navigation-pickup">
                  🧭 Itinéraire vers la récupération
                </Button>
                <Button onClick={() => setPickupProofOpen(true)} disabled={!m.assignment_confirmed_at}
                  className="w-full rounded-md bg-[#1F4032] text-white hover:bg-[#2A5443]"
                  data-testid="status-loaded">Marchandise chargée</Button>
              </>
            )}
            {isTransporter && m.status === "accepted" && m.payment_status !== "paid" && (
              <>
                <Button
                  onClick={() => { setNavPhase("pickup"); setNavOpen(true); }}
                  variant="outline"
                  className="w-full rounded-md border-[#1F4032] text-[#1F4032] hover:bg-[#1F4032]/5 mb-2 gap-2"
                  data-testid="open-navigation-pickup-wait">
                  🧭 Voir l&apos;itinéraire vers la récupération
                </Button>
                <div className="text-sm text-amber-700 px-2 py-2 bg-amber-50 rounded-md">
                  ⏳ En attente du paiement de l&apos;expéditeur — vous pouvez consulter l&apos;itinéraire mais attendez la confirmation de paiement avant de partir.
                </div>
              </>
            )}
            {isTransporter && m.payment_status === "paid" && m.assignment_confirmed_at
              && ["accepted", "loaded", "en_route"].includes(m.status) && (
                <DriverLocationControl mission={m} onChanged={load} />
            )}
            {isTransporter && m.status === "loaded" && (
              <Button onClick={() => updateStatus("en_route")}
                className="w-full rounded-md bg-[#1F4032] text-white hover:bg-[#2A5443]"
                data-testid="status-en-route">Démarrer le trajet</Button>
            )}
            {isTransporter && m.status === "en_route" && (
              <>
                <Button
                  onClick={() => { setNavPhase("delivery"); setNavOpen(true); }}
                  variant="outline"
                  className="w-full rounded-md border-[#1F4032] text-[#1F4032] hover:bg-[#1F4032]/5 mb-2 gap-2"
                  data-testid="open-navigation">
                  🧭 Lancer la navigation GPS
                </Button>
                <Button onClick={() => setProofOpen(true)}
                  className="w-full rounded-md bg-[#1F4032] hover:bg-[#2A5443]"
                  data-testid="status-delivered">Marquer livrée</Button>
              </>
            )}
            {isTransporter && m.status === "loaded" && (
              <Button
                onClick={() => { setNavPhase("delivery"); setNavOpen(true); }}
                variant="outline"
                className="w-full rounded-md border-[#1F4032] text-[#1F4032] hover:bg-[#1F4032]/5 mb-2 gap-2"
                data-testid="open-navigation-loaded">
                🧭 Reprendre la navigation
              </Button>
            )}
            {/* Confirmation côté expéditeur (après livraison transporteur) */}
            {m.status === "delivered" && user?.id === m.shipper_id && !m.shipper_confirmed && (
              <div className="border-2 border-[#1F4032] bg-[#1F4032]/5 rounded-md p-4 mb-2">
                <div className="overline text-[#1F4032] mb-1">Action requise</div>
                <div className="text-sm mb-3">
                  Le transporteur a confirmé la livraison. Vérifiez le justificatif ci-dessous,
                  puis <b>signez à votre tour</b> pour libérer le paiement de
                  <b> {m.transporter_amount}€</b> au transporteur (commission plateforme {m.platform_commission}€).
                </div>
                <Button
                  onClick={() => setShipperConfirmOpen(true)}
                  className="w-full rounded-md bg-[#1F4032] hover:bg-[#2A5443]"
                  data-testid="shipper-confirm-btn">
                  ✓ Confirmer la réception et signer
                </Button>
              </div>
            )}
            {m.status === "delivered" && user?.id === m.transporter_id && !m.shipper_confirmed && (
              <div className="text-sm text-amber-700 px-3 py-2 bg-amber-50 rounded-md mb-2">
                ⏳ En attente de la confirmation de l&apos;expéditeur. Le paiement sera libéré dès qu&apos;il aura signé à son tour.
              </div>
            )}
            {m.status === "delivered" && m.shipper_confirmed && (
              <div className="text-sm text-emerald-700 px-3 py-2 bg-emerald-50 rounded-md mb-2 flex items-center gap-2">
                <CheckCircle2 className="w-4 h-4" />
                Réception confirmée — le statut de paiement indique l’avancement du versement
              </div>
            )}
            {/* Notation seulement après double confirmation */}
            {m.status === "delivered" && m.shipper_confirmed && canChat && (
              <RatingBlock missionId={id} />
            )}
            {/* Annulation : expéditeur ou transporteur tant que non livrée/annulée */}
            {canChat && ["open", "accepted"].includes(m.status) && (
              <Button
                onClick={() => setCancelOpen(true)}
                variant="outline"
                className="w-full rounded-md border-red-300 text-red-700 hover:bg-red-50 mt-2"
                data-testid="cancel-mission">
                Annuler la mission
              </Button>
            )}
            {canReportAbsence && (
              <Button
                onClick={reportAbsence}
                variant="outline"
                className="w-full rounded-md border-amber-400 text-amber-800 hover:bg-amber-50 mt-2"
                data-testid="report-transporter-absence">
                Signaler une absence au rendez-vous
              </Button>
            )}
            {m.status === "cancelled" && (
              <div className="text-sm text-red-700 px-3 py-2 bg-red-50 rounded-md">
                ❌ Annulée{m.cancellation_reason ? ` — ${m.cancellation_reason}` : ""}
                {m.payment_status === "refund_pending" && <div className="text-xs mt-1">Remboursement en cours</div>}
              </div>
            )}
            {m.status === "delivered" && (m.proof_photo || m.proof_signature) && (
              <div className="mt-3 pt-3 border-t border-[#1F4032]/10 space-y-3">
                <div>
                  <div className="overline mb-2">Justificatif livraison (transporteur)</div>
                  {m.proof_photo && (
                    <img src={m.proof_photo} alt="Preuve livraison" className="w-full rounded-md mb-2 border border-[#1F4032]/10" data-testid="proof-photo" />
                  )}
                  {m.proof_signature && (
                    <div className="border border-[#1F4032]/10 rounded-md bg-[#FBF7F1] p-2">
                      <img src={m.proof_signature} alt="Signature transporteur" className="w-full" data-testid="proof-signature" />
                      {m.proof_signed_name && (
                        <div className="text-xs text-muted-foreground mt-1">Signé par : <b>{m.proof_signed_name}</b></div>
                      )}
                      {m.proof_reserves && (
                        <div className="text-xs text-muted-foreground mt-1">Réserves : <b>{m.proof_reserves}</b></div>
                      )}
                    </div>
                  )}
                </div>
                {m.shipper_confirmed && m.shipper_signature && (
                  <div>
                    <div className="overline mb-2">Confirmation expéditeur</div>
                    <div className="border border-[#1F4032]/10 rounded-md bg-[#FBF7F1] p-2">
                      <img src={m.shipper_signature} alt="Signature expéditeur" className="w-full" data-testid="shipper-signature" />
                      {m.shipper_signed_name && (
                        <div className="text-xs text-muted-foreground mt-1">Signé par : <b>{m.shipper_signed_name}</b></div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      <MissionDisputePanel mission={m} onChange={load} />
      <MissionInvoices mission={m} />

      <ProofOfDeliveryDialog
        open={proofOpen}
        onOpenChange={setProofOpen}
        onConfirm={confirmDelivery}
        loading={proofLoading}
      />

      <PickupProofDialog
        open={pickupProofOpen}
        onOpenChange={setPickupProofOpen}
        onConfirm={confirmPickup}
        loading={pickupProofLoading}
      />

      <Dialog open={!!selectionCandidate} onOpenChange={(open) => { if (!open) setSelectionCandidate(null); }}>
        <DialogContent className="max-w-md" data-testid="select-transporter-dialog">
          <DialogHeader>
            <DialogTitle>Confirmer le transporteur</DialogTitle>
            <p className="text-sm text-muted-foreground">
              Vérifiez le profil avant de confirmer. Le choix verrouille le transporteur et le prix de cette mission.
            </p>
          </DialogHeader>
          {selectionCandidate && (
            <div className="space-y-3 text-sm">
              <div className="border border-[#1F4032]/10 rounded-md p-3">
                <div className="font-semibold">{selectionCandidate.transporter_name}</div>
                <div className="text-xs text-neutral-500 mt-1">
                  {selectionCandidate.transporter_rating?.count > 0
                    ? `Note ${Number(selectionCandidate.transporter_rating.average || 0).toFixed(1)}/5 · ${selectionCandidate.transporter_rating.count} avis`
                    : "Aucun avis de mission"}
                  {selectionCandidate.transporter_delivered != null && ` · ${selectionCandidate.transporter_delivered} livraison(s)`}
                </div>
              </div>
              <div className="grid grid-cols-2 gap-2 text-xs">
                <div className="bg-neutral-50 rounded-md p-2">Prix total<br /><b className="text-sm">{Number(m.price || 0).toFixed(2)} €</b></div>
                <div className="bg-neutral-50 rounded-md p-2">Transporteur (85 %)<br /><b className="text-sm">{Number(m.transporter_amount || 0).toFixed(2)} €</b></div>
                <div className="bg-neutral-50 rounded-md p-2">Commission MML (15 %)<br /><b className="text-sm">{Number(m.platform_commission || 0).toFixed(2)} €</b></div>
                <div className="bg-neutral-50 rounded-md p-2">Validité candidature<br /><b className="text-sm">{selectionCandidate.validity_minutes ?? (selectionCandidate.seconds_left != null ? Math.max(0, Math.floor(selectionCandidate.seconds_left / 60)) : "—")} min</b></div>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="ghost" onClick={() => setSelectionCandidate(null)}>Retour</Button>
            <Button
              onClick={async () => {
                const candidate = selectionCandidate;
                setSelectionCandidate(null);
                await selectTransporter(candidate.transporter_id);
              }}
              className="bg-[#1F4032] hover:bg-[#2A5443]"
              data-testid="select-transporter-confirm">
              Confirmer le choix
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={contractOpen} onOpenChange={setContractOpen}>
        <DialogContent className="max-w-lg" data-testid="contract-dialog">
          <DialogHeader>
            <DialogTitle>Contrat de transport — conditions sélectionnées</DialogTitle>
            <p className="text-sm text-muted-foreground">
              Ces conditions sont figées au moment du choix du transporteur. Le contrat est formé après confirmation du paiement ; toute modification se traite par un nouveau contrat ou un avenant accepté par les parties.
            </p>
          </DialogHeader>
          {contract && (
            <div className="grid grid-cols-2 gap-3 text-sm">
              <div className="col-span-2 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-amber-900">
                <span className="font-semibold">Statut : </span>
                {contract.status === "pending_payment" ? "en attente du paiement" :
                  contract.status === "formed" ? "contrat formé" :
                  contract.status === "replaced" ? "remplacé" :
                  contract.status === "cancelled" ? "annulé" : contract.status || "—"}
              </div>
              <div><div className="overline text-muted-foreground">Expéditeur</div><b>{contract.shipper_name || "—"}</b></div>
              <div><div className="overline text-muted-foreground">Transporteur</div><b>{contract.transporter_name || "—"}</b></div>
              <div className="col-span-2"><div className="overline text-muted-foreground">Trajet</div><div>{contract.pickup_address} → {contract.delivery_address}</div></div>
              <div><div className="overline text-muted-foreground">Enlèvement</div><div>{contract.pickup_date?.slice(0, 16).replace("T", " ") || "—"}</div></div>
              <div><div className="overline text-muted-foreground">Mode</div><div>Transport dédié</div></div>
              <div><div className="overline text-muted-foreground">Marchandise</div><div>{contract.cargo_weight_kg} kg · {contract.cargo_volume_m3} m³</div></div>
              <div><div className="overline text-muted-foreground">Véhicule</div><div>{VEHICLE_LABEL[contract.vehicle_type] || contract.vehicle_type}</div></div>
              <div><div className="overline text-muted-foreground">Prix total</div><b>{Number(contract.price || 0).toFixed(2)} €</b></div>
              <div><div className="overline text-muted-foreground">Ventilation</div><div>{Number(contract.transporter_amount || 0).toFixed(2)} € transporteur · {Number(contract.platform_commission || 0).toFixed(2)} € MML</div></div>
              <div className="col-span-2 text-xs text-muted-foreground border-t pt-3">
                Instantané v{contract.version || 1} · sélectionné le {contract.selected_at ? new Date(contract.selected_at).toLocaleString("fr-FR") : "—"} · formé le {contract.formed_at ? new Date(contract.formed_at).toLocaleString("fr-FR") : "après paiement"} · valeur déclarée {contract.declared_value_eur != null ? `${contract.declared_value_eur} €` : "non renseignée"}
              </div>
            </div>
          )}
          <DialogFooter><Button variant="ghost" onClick={() => setContractOpen(false)}>Fermer</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={cancelOpen} onOpenChange={(v) => { setCancelOpen(v); if (!v) setCancelReason(""); }}>
        <DialogContent className="max-w-md" data-testid="cancel-dialog">
          <DialogHeader>
            <DialogTitle>Annuler cette mission ?</DialogTitle>
            <p className="text-sm text-muted-foreground">
              Cette action est <b>définitive</b>. {m.payment_status === "paid" ? "Le paiement sera remboursé." : "Aucun paiement n'a été effectué."}
            </p>
          </DialogHeader>
          <div>
            <Label className="overline">Motif (optionnel)</Label>
            <Textarea
              value={cancelReason}
              onChange={(e) => setCancelReason(e.target.value)}
              placeholder="Raison de l'annulation…"
              className="mt-1"
              rows={3}
              data-testid="cancel-reason"
            />
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setCancelOpen(false)} disabled={cancelLoading}>
              Garder la mission
            </Button>
            <Button
              onClick={cancelMission}
              disabled={cancelLoading}
              className="bg-red-600 hover:bg-red-700"
              data-testid="cancel-confirm">
              {cancelLoading ? "Annulation…" : "Confirmer l'annulation"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <NavigationView
        open={navOpen}
        onOpenChange={setNavOpen}
        mission={m}
        phase={navPhase}
        onArrived={() => { /* le transporteur doit confirmer manuellement la livraison */ }}
      />

      <ShipperConfirmDialog
        open={shipperConfirmOpen}
        onOpenChange={setShipperConfirmOpen}
        onConfirm={handleShipperConfirm}
        loading={shipperConfirmLoading}
        transporterAmount={m.transporter_amount || 0}
        commission={m.platform_commission || 0}
      />
    </div>
  );
}

function RatingBlock({ missionId }) {
  const [score, setScore] = useState(5);
  const [comment, setComment] = useState("");
  const [done, setDone] = useState(false);
  const submit = async () => {
    try {
      await api.post(`/missions/${missionId}/rate`, { score, comment });
      toast.success("Note enregistrée");
      setDone(true);
    } catch (e) { toast.error(formatApiError(e)); }
  };
  if (done) return <div className="text-sm text-green-700 flex items-center gap-2"><Star className="w-4 h-4" /> Note envoyée</div>;
  return (
    <div className="space-y-2 pt-2" data-testid="rating-block">
      <div className="overline">Noter (1-5)</div>
      <div className="flex gap-1">
        {[1,2,3,4,5].map((n) => (
          <button key={n} onClick={() => setScore(n)}
            className={`w-8 h-8 ${n <= score ? "bg-[#1F4032] text-white" : "bg-muted"} text-sm`}>{n}</button>
        ))}
      </div>
      <Textarea value={comment} onChange={(e) => setComment(e.target.value)}
        placeholder="Commentaire" className="rounded-md" />
      <Button onClick={submit} className="w-full rounded-md bg-[#1F4032] text-white">Envoyer ma note</Button>
    </div>
  );
}
